/**
 * UI state for the one-tap "Start together" / Invite action, shared by
 * every surface that offers it (the top-bar Invite button and the
 * onboarding coach bar) so they narrate the same run and a redirect
 * sign-in resumes exactly once.
 */

import { useEffect, useRef, useSyncExternalStore } from "react";
import { AuthError, getCurrentUser, useAuth } from "./auth";
import {
  collabLimitFromError,
  shareInviteLink,
  type InviteShareMethod,
} from "./sharedInvites";
import {
  isStartTogetherPending,
  startTogether,
  StartTogetherError,
  StartTogetherRedirecting,
  type StartTogetherStep,
} from "./startTogether";
import { requestUpsell } from "./upsellRequests";

export interface StartTogetherUiState {
  busyStep: StartTogetherStep | null;
  /** A real failure the visitor may need to act on. */
  error: string | null;
  /** Neutral outcome (cancelled sign-in, limit reached) — not an error. */
  notice: string | null;
  /** How the fresh link left the device, for a transient confirmation. */
  shared: InviteShareMethod | null;
}

const IDLE: StartTogetherUiState = { busyStep: null, error: null, notice: null, shared: null };

let state: StartTogetherUiState = IDLE;
const listeners = new Set<() => void>();
let sharedTimer: ReturnType<typeof setTimeout> | null = null;

function set(patch: Partial<StartTogetherUiState>) {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

function flashShared(method: InviteShareMethod | null) {
  if (sharedTimer) clearTimeout(sharedTimer);
  set({ shared: method });
  if (method) sharedTimer = setTimeout(() => set({ shared: null }), 2600);
}

export const CANCELLED_NOTICE = "No problem — tap Invite when you're ready.";
export const LIMIT_NOTICE = "Free covers one shared room. Upgrade to share more.";

/** Run sign in → save → share → link, then hand the link to the OS. */
export async function runStartTogether(): Promise<void> {
  if (state.busyStep) return;
  set({ error: null, notice: null, shared: null });
  try {
    const res = await startTogether({ onStep: (busyStep) => set({ busyStep }) });
    set({ busyStep: null });
    const method = await shareInviteLink(res.link, {
      roomName: res.roomName,
      from: getCurrentUser()?.displayName ?? null,
    });
    flashShared(method);
  } catch (err) {
    if (err instanceof StartTogetherRedirecting) return; // page is leaving
    set({ busyStep: null });
    if (collabLimitFromError(err)) {
      set({ notice: LIMIT_NOTICE });
      requestUpsell("collab_unlimited", "limit");
      return;
    }
    if (
      err instanceof StartTogetherError &&
      err.cause instanceof AuthError &&
      err.cause.kind === "cancelled"
    ) {
      set({ notice: CANCELLED_NOTICE });
      return;
    }
    set({
      error: err instanceof StartTogetherError ? err.message : "Something went wrong. Try again.",
    });
  }
}

/** Re-open the share sheet for a link that already exists. */
export async function reshareInvite(
  link: string,
  opts: { roomName?: string; from?: string | null } = {}
): Promise<void> {
  flashShared(await shareInviteLink(link, opts));
}

export function clearStartTogetherMessages(): void {
  if (state.error || state.notice) set({ error: null, notice: null });
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useStartTogetherState(): StartTogetherUiState {
  return useSyncExternalStore(subscribe, () => state, () => IDLE);
}

/**
 * Finish a run whose sign-in went through a full-page redirect. Mount once
 * (App); every surface reading {@link useStartTogetherState} shows it.
 */
export function useResumeStartTogether(): void {
  const status = useAuth().status;
  const resumedRef = useRef(false);
  useEffect(() => {
    if (status !== "signed-in" || resumedRef.current || !isStartTogetherPending()) return;
    resumedRef.current = true;
    const t = setTimeout(() => void runStartTogether(), 0);
    return () => clearTimeout(t);
  }, [status]);
}

export const START_TOGETHER_PROGRESS: Record<StartTogetherStep, string> = {
  sign_in: "Signing in…",
  save: "Saving your room…",
  promote: "Opening a shared room…",
  invite: "Creating your invite link…",
  open: "Going live…",
};

/** Test-only. */
export function _resetStartTogetherControllerForTests(): void {
  if (sharedTimer) clearTimeout(sharedTimer);
  sharedTimer = null;
  state = IDLE;
  listeners.clear();
}
